import { useState, useMemo } from "react";
import {
  Lock, Loader2, Phone, MessageCircle, Car,
} from "lucide-react";
import { LOGO_URL } from "@/lib/constants";
import { useAdminAuth, useLeads } from "@/hooks/useAdminAuth";
import { FilterBar, DashboardHeader, LeadsList, STATUSES } from "@/components/admin/DashboardParts";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  new: "bg-[#e10600]/15 text-[#ff6b66] border-[#e10600]/40",
  contacted: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  closed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const waNumber = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.length === 8 ? `965${digits}` : digits;
};

const PinLogin = ({ pin, setPin, onSubmit, error, busy }) => (
  <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4" data-testid="admin-login-page">
    <form onSubmit={onSubmit} className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#111] p-8 text-center">
      <span className="bg-white rounded-2xl px-4 py-2 inline-flex items-center mb-6">
        <img src={LOGO_URL} alt="Dent Xpress" className="h-10 w-auto" />
      </span>
      <h1 className="font-display font-black text-2xl flex items-center justify-center gap-2">
        <Lock className="w-5 h-5 text-[#e10600]" /> Admin Access
      </h1>
      <p className="mt-2 text-sm text-white/50">Enter your PIN to view leads</p>
      <input
        type="password"
        inputMode="numeric"
        value={pin}
        onChange={(e) => setPin(e.target.value)}
        placeholder="••••••"
        className="mt-6 w-full rounded-xl bg-white/5 border border-white/10 focus:border-[#e10600]/60 outline-none px-4 py-3.5 text-center text-xl tracking-[0.5em] placeholder:tracking-normal"
        autoFocus
        data-testid="admin-pin-input"
      />
      {error && <p className="mt-3 text-sm text-red-400 font-semibold" data-testid="admin-login-error">{error}</p>}
      <button
        type="submit"
        disabled={busy || !pin}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#e10600] hover:bg-[#c20500] disabled:opacity-50 font-bold transition"
        data-testid="admin-login-btn"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Unlock"}
      </button>
    </form>
  </div>
);

const LeadCard = ({ lead, token, onStatusChange }) => (
  <div className="rounded-2xl border border-white/10 bg-[#111] p-5" data-testid={`admin-lead-card-${lead.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display font-bold text-lg">{lead.name}</h3>
          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase border ${STATUS_STYLES[lead.status] || ""}`}>
            {lead.status}
          </span>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase bg-white/5 border border-white/10 text-white/50">
            {lead.language === "ar" ? "عربي" : "EN"}
          </span>
        </div>
        <p className="mt-1 text-xs text-white/40">{formatDate(lead.created_at)}</p>
      </div>
      <div className="flex gap-2">
        <a
          href={`https://wa.me/${waNumber(lead.phone)}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] text-sm font-bold hover:bg-[#25D366]/25 transition"
          data-testid={`admin-lead-whatsapp-${lead.id}`}
        >
          <MessageCircle className="w-4 h-4" /> {lead.phone}
        </a>
        <a
          href={`tel:+${waNumber(lead.phone)}`}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 hover:bg-white/5 transition"
          data-testid={`admin-lead-call-${lead.id}`}
          aria-label="Call"
        >
          <Phone className="w-4 h-4" />
        </a>
      </div>
    </div>

    {(lead.car || lead.description) && (
      <div className="mt-3 space-y-1.5 text-sm text-white/70">
        {lead.car && (
          <p className="flex items-center gap-2"><Car className="w-4 h-4 text-white/40 shrink-0" /> {lead.car}</p>
        )}
        {lead.description && <p className="text-white/60">{lead.description}</p>}
      </div>
    )}

    {lead.photo_paths && lead.photo_paths.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-2">
        {lead.photo_paths.map((p, i) => (
          <a
            key={p}
            href={`${API}/api/admin/photos/${p}?auth=${token}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`admin-lead-photo-${lead.id}-${i}`}
          >
            <img
              src={`${API}/api/admin/photos/${p}?auth=${token}`}
              alt={`Dent photo ${i + 1}`}
              className="w-24 h-24 rounded-xl object-cover border border-white/15 hover:border-[#e10600]/60 transition"
              loading="lazy"
            />
          </a>
        ))}
      </div>
    )}

    <div className="mt-4 flex gap-2">
      {STATUSES.map((s) => (
        <button
          key={s}
          onClick={() => onStatusChange(lead.id, s)}
          disabled={lead.status === s}
          className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize border transition ${
            lead.status === s
              ? `${STATUS_STYLES[s]} cursor-default`
              : "border-white/10 text-white/50 hover:text-white hover:border-white/30"
          }`}
          data-testid={`admin-lead-status-${lead.id}-${s}`}
        >
          {s}
        </button>
      ))}
    </div>
  </div>
);

export default function Admin() {
  const { token, logout, pin, setPin, loginError, loggingIn, login } = useAdminAuth();
  const { leads, loadLeads, updateStatus } = useLeads(token, logout);
  const [filter, setFilter] = useState("all");

  const { filtered, counts } = useMemo(() => {
    const list = leads || [];
    return {
      filtered: list.filter((l) => filter === "all" || l.status === filter),
      counts: list.reduce((acc, l) => ({ ...acc, [l.status]: (acc[l.status] || 0) + 1 }), {}),
    };
  }, [leads, filter]);

  if (!token) {
    return <PinLogin pin={pin} setPin={setPin} onSubmit={login} error={loginError} busy={loggingIn} />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="admin-dashboard">
      <DashboardHeader
        leadsCount={leads ? leads.length : null}
        onRefresh={loadLeads}
        onLogout={logout}
      />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <FilterBar filter={filter} setFilter={setFilter} counts={counts} />
        <LeadsList
          leads={leads}
          filtered={filtered}
          renderCard={(lead) => (
            <LeadCard key={lead.id} lead={lead} token={token} onStatusChange={updateStatus} />
          )}
        />
      </main>
    </div>
  );
}
